import { useEffect, useState } from 'react';

import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { ptBR as pickersPtBR } from '@mui/x-date-pickers/locales';
import {
  addMonths,
  format,
  getWeeksInMonth,
  isSameMonth,
  startOfMonth,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';

import TimelineCalendarMonth, {
  formatCalendarMonth,
} from './TimelineCalendarMonth';
import { TimelineDateRange } from '../calendarTypes';
import useTimelineCalendarDays from '../hooks/useTimelineCalendarDays';
import {
  fromCivilDate,
  getCalendarWindow,
  getInitialVisibleMonth,
  nextRangeSelection,
} from '../timelineCalendarDates';
import { getTimelineTomorrow, TimelineFilters } from '../timelineData';

interface TimelineFutureCalendarProps {
  operationId?: string;
  userId: number | null;
  search: string;
  isSearchPending: boolean;
  filters: TimelineFilters;
  onDateRangeChange: (range: TimelineDateRange) => void;
}

const TITLE_ID = 'timeline-future-calendar-title';

// Valores em px explícitos: no sx do MUI, números <= 1 viram frações (100%).
const visuallyHidden = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute' as const,
  whiteSpace: 'nowrap' as const,
  width: '1px',
};

export default function TimelineFutureCalendar({
  operationId,
  userId,
  search,
  isSearchPending,
  filters,
  onDateRangeChange,
}: TimelineFutureCalendarProps) {
  const theme = useTheme();
  const isWide = useMediaQuery(theme.breakpoints.up('md'));
  const monthsVisible = isWide ? 2 : 1;
  const tomorrow = getTimelineTomorrow();
  const firstMonth = startOfMonth(fromCivilDate(tomorrow));
  const [visibleMonth, setVisibleMonth] = useState(() =>
    getInitialVisibleMonth(filters.dateStart, tomorrow),
  );
  const [anchor, setAnchor] = useState<string | null>(null);
  const { dateStart, dateEnd } = filters;
  const months = Array.from({ length: monthsVisible }, (_, index) =>
    addMonths(visibleMonth, index),
  );
  const calendarWindow = getCalendarWindow(
    visibleMonth,
    monthsVisible,
    tomorrow,
  );
  const { eventDays, hasData, isError, isLoading, retry } =
    useTimelineCalendarDays({
      enabled: !isSearchPending,
      operationId,
      userId,
      start: calendarWindow.start,
      end: calendarWindow.end,
      search,
      filters,
    });

  useEffect(() => {
    // Uma alteração externa do período (chip, drawer, histórico) encerra a
    // seleção iniciada no calendário.
    if (anchor && (dateStart !== anchor || dateEnd !== anchor)) {
      setAnchor(null);
    }
  }, [anchor, dateEnd, dateStart]);

  const selectDay = (day: string) => {
    const selection = nextRangeSelection(anchor, day);
    setAnchor(selection.anchor);
    onDateRangeChange(selection.range);
  };
  const clearDates = () => {
    setAnchor(null);
    onDateRangeChange({ dateStart: '', dateEnd: '' });
  };
  const hasDateFilter = Boolean(dateStart || dateEnd);
  const canGoBack = !isSameMonth(visibleMonth, firstMonth);
  const monthsLabel = months.map(formatCalendarMonth).join(' e ');
  const weeks = Math.max(
    ...months.map((month) => getWeeksInMonth(month, { locale: ptBR })),
  );
  const anchorLabel = anchor
    ? `Início selecionado: ${format(fromCivilDate(anchor), "d 'de' MMMM", {
        locale: ptBR,
      })}. Escolha a data final.`
    : '';

  return (
    <Box
      component="section"
      role="region"
      aria-labelledby={TITLE_ID}
      data-testid="timeline-future-calendar"
      onKeyDown={(event) => {
        if (event.key === 'Escape' && anchor) setAnchor(null);
      }}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        p: 1.5,
        width: 'fit-content',
        maxWidth: '100%',
        minWidth: 0,
      }}>
      <Stack spacing={1}>
        <Stack
          direction="row"
          flexWrap="wrap"
          alignItems="center"
          justifyContent="space-between"
          sx={{ columnGap: 2, rowGap: 0.5 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              id={TITLE_ID}
              component="h2"
              variant="subtitle1"
              fontWeight={600}
              lineHeight={1.3}>
              Calendário de eventos
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Selecione um dia ou um período para filtrar os eventos futuros.
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {hasDateFilter && (
              <Button size="small" variant="text" onClick={clearDates}>
                Limpar datas
              </Button>
            )}
            <IconButton
              size="small"
              aria-label="Mês anterior"
              disabled={!canGoBack}
              onClick={() => setVisibleMonth((month) => addMonths(month, -1))}>
              <ChevronLeft />
            </IconButton>
            <IconButton
              size="small"
              aria-label="Próximo mês"
              onClick={() => setVisibleMonth((month) => addMonths(month, 1))}>
              <ChevronRight />
            </IconButton>
          </Stack>
        </Stack>
        <Box
          aria-live="polite"
          data-testid="timeline-future-calendar-live"
          sx={visuallyHidden}>
          {`Exibindo ${monthsLabel}. ${anchorLabel}`}
        </Box>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={ptBR}
          localeText={
            pickersPtBR.components.MuiLocalizationProvider.defaultProps
              .localeText
          }>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'center', md: 'flex-start' }}
            sx={{ gap: { xs: 1, md: 2 } }}>
            {months.map((month) => (
              <TimelineCalendarMonth
                key={format(month, 'yyyy-MM')}
                month={month}
                minDate={tomorrow}
                eventDays={eventDays}
                range={{ dateStart, dateEnd }}
                loading={isLoading}
                weeks={weeks}
                onSelectDay={selectDay}
              />
            ))}
          </Stack>
        </LocalizationProvider>
        <Stack
          direction="row"
          flexWrap="wrap"
          alignItems="center"
          sx={{ columnGap: 2, rowGap: 0.5 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box
              aria-hidden="true"
              sx={{
                width: 6,
                height: 6,
                borderRadius: 2,
                bgcolor: 'primary.main',
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Dia com eventos
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box
              aria-hidden="true"
              data-testid="timeline-calendar-range-swatch"
              sx={({ palette }) => ({
                width: 20,
                height: 12,
                bgcolor: alpha(palette.text.primary, 0.06),
              })}
            />
            <Typography variant="caption" color="text.secondary">
              Período selecionado
            </Typography>
          </Stack>
          {hasData && !isError && eventDays.size === 0 && (
            <Typography role="status" variant="caption" color="text.secondary">
              Nenhum evento nos meses exibidos.
            </Typography>
          )}
        </Stack>
        {isError && (
          <Alert
            severity="warning"
            sx={{ py: 0, alignItems: 'center' }}
            action={
              <Button color="inherit" size="small" onClick={() => retry()}>
                Tentar novamente
              </Button>
            }>
            Não foi possível carregar os dias com eventos. Você ainda pode
            selecionar datas.
          </Alert>
        )}
      </Stack>
    </Box>
  );
}
